import {
    Bike,
    Car,
    Truck,
    Home,
    Landmark,
    ShieldCheck,
    ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { waLink } from "@/lib/businessInfo";

const SERVICES = [
    { key: "vehicle", Icon: Bike, testId: "service-vehicle" },
    { key: "car", Icon: Car, testId: "service-car" },
    { key: "commercial", Icon: Truck, testId: "service-commercial" },
    { key: "home", Icon: Home, testId: "service-home" },
    { key: "mortgage", Icon: Landmark, testId: "service-mortgage" },
    { key: "insurance", Icon: ShieldCheck, testId: "service-insurance" },
];

const Services = () => {
    const { lang, t } = useLanguage();
    const gu = lang === "gu" ? "font-gujarati" : "";

    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 md:py-32 bg-[#F8FAFC] relative"
        >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <span
                        className={`inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0B308A] ${gu}`}
                    >
                        {t("services.overline")}
                    </span>
                    <h2
                        className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 leading-[1.1] ${gu}`}
                    >
                        {t("services.title")}
                    </h2>
                    <p className={`mt-5 text-slate-600 text-lg leading-relaxed ${gu}`}>
                        {t("services.subtitle")}
                    </p>
                </div>

                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {SERVICES.map(({ key, Icon, testId }, idx) => (
                        <a
                            key={key}
                            data-testid={testId}
                            href={waLink(
                                `Hello, I'm interested in ${t(`services.items.${key}.title`)}. Please share details.`,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative bg-white border border-slate-100 hover:border-[#0B308A]/30 transition-all duration-300 p-8 hover:-translate-y-1 shadow-sm hover:shadow-[0_20px_40px_rgba(11,48,138,0.10)] flex flex-col rounded-sm"
                            style={{ animationDelay: `${idx * 60}ms` }}
                        >
                            <div className="w-12 h-12 rounded-sm bg-[#EFF6FF] text-[#0B308A] flex items-center justify-center group-hover:bg-[#0B308A] group-hover:text-white transition-colors">
                                <Icon className="w-6 h-6" strokeWidth={2} />
                            </div>
                            <h3
                                className={`mt-6 font-display font-semibold text-xl text-slate-900 ${gu}`}
                            >
                                {t(`services.items.${key}.title`)}
                            </h3>
                            <p
                                className={`mt-3 text-[14.5px] text-slate-600 leading-relaxed flex-1 ${gu}`}
                            >
                                {t(`services.items.${key}.desc`)}
                            </p>
                            <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0B308A] group-hover:gap-2.5 transition-all">
                                <span className={gu}>{t("services.cta")}</span>
                                <ArrowUpRight
                                    className="w-4 h-4 group-hover:rotate-12 transition-transform"
                                    strokeWidth={2.4}
                                />
                            </div>
                            <span className="absolute top-0 right-0 h-1 w-0 bg-[#0B308A] group-hover:w-full transition-all duration-500" />
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Services;
